import { Inject, Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { AccessManagementUserAuthContext } from '@cdw-ae/access-management-common-services';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import {
  catchError,
  filter,
  from,
  map,
  of,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs';
import { ACCESS_MANAGEMENT_FRONTEND_AUTH_SERVICE } from '../../app.tokens';
import { GoogleTagManagerService } from '../../common/services/analytics/google-tag-manager.service';
import { AuthenticationService } from '../../common/services/authentication/authentication.service';
import * as AppLifecyclesActions from '../app-lifecycles/app-lifecycles.actions';
import { extractPermissionCodes } from './permission-codes.util';
import * as UserIdentitySettingsActions from './user-identity-settings.actions';
import { selectCurrentUserData } from './user-identity-settings.selectors';

@Injectable()
export class UserIdentitySettingsEffects {
  private actions$ = inject(Actions);
  private authenticationService = inject(AuthenticationService);
  private router = inject(Router);
  private store = inject(Store);
  private gtmService = inject(GoogleTagManagerService);

  constructor(
    @Inject(ACCESS_MANAGEMENT_FRONTEND_AUTH_SERVICE)
    private accessManagementAuthService: {
      loadEffectivePermissions(): Promise<AccessManagementUserAuthContext>;
      authContext: AccessManagementUserAuthContext | null;
    }
  ) {}

  init$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserIdentitySettingsActions.initUserIdentitySettings)
      ),
    { dispatch: false }
  );

  triggerPostLogin$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserIdentitySettingsActions.setUser),
        filter((action) => !!action.userData),
        tap(() => {
          this.authenticationService.postLogin();
        })
      ),
    { dispatch: false }
  );

  triggerAuthCompleted$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserIdentitySettingsActions.setUser),
      filter(
        (action) =>
          !!action.userData && !!action.userData.claims?.core?.customerId
      ),
      map(() => AppLifecyclesActions.authCompleted())
    )
  );

  loadPermissionsAfterAuth$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppLifecyclesActions.authCompleted),
      switchMap(() =>
        from(this.accessManagementAuthService.loadEffectivePermissions()).pipe(
          map((permissions) =>
            UserIdentitySettingsActions.loadPermissionsSuccess({ permissions })
          ),
          catchError((error: unknown) => {
            const errorMessage =
              error instanceof Error
                ? error.message
                : 'Failed to load permissions';
            return of(
              UserIdentitySettingsActions.loadPermissionsFailure({
                error: errorMessage,
              })
            );
          })
        )
      )
    )
  );

  pushUserIdentifiedAfterPermissions$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserIdentitySettingsActions.loadPermissionsSuccess),
        withLatestFrom(this.store.select(selectCurrentUserData)),
        tap(([{ permissions }, userData]) => {
          if (!permissions) {
            return;
          }

          // GA4 event — must stay PII-free (no email/name). EDUS-2571 sends
          // only the email domain; EDUS-2479 guards this with a test.
          // Bridge key written by onboarding-setup.component.ts before reresolveAndRoute().
          const justCompleted =
            sessionStorage.getItem('onboarding_just_completed') === 'true';
          if (justCompleted) {
            sessionStorage.removeItem('onboarding_just_completed');
          }

          this.gtmService.pushUserIdentified({
            customer_id: permissions.sqlCustomerId,
            customer_name: userData?.claims?.core?.customer?.name ?? '',
            user_id: userData?.uid ?? '',
            session_id: userData?.claims?.core?.sessionId ?? '',
            ...(userData?.email && {
              customer_domain: userData.email.split('@')[1],
            }),
            is_login_after_onboarding: justCompleted,
          });

          // Userlane-only event — PII allowed here. GTM maps these to the
          // Userlane identify tag and must NOT forward them to GA4.
          // See docs/frontend/userlane-gtm-attributes.md.
          //
          // Contract: the app is the source of clean data — every optional
          // field is OMITTED when absent (never sent as '' / null / []), because
          // Userlane rejects an attribute with no value (422). GTM translates
          // (renames/coerces to Userlane's schema) and passes through; it must
          // never fabricate a default. isAdmin/isDelegatedAdmin are intentionally
          // absent: they are not on the logged-in user's claims today and are
          // deferred to a backend follow-up.
          const userlaneEmail = userData?.email ?? permissions.userEmail ?? '';
          // `permissions.permissions` arrives from loadEffectivePermissions() as a
          // JSON string (not the parsed Map), so extract codes tolerantly.
          const permissionCodes = extractPermissionCodes(
            permissions.permissions as unknown
          );
          this.gtmService.pushUserlaneIdentify({
            user_id: userData?.uid ?? '',
            customer_id: permissions.sqlCustomerId,
            ...(userData?.claims?.core?.customer?.name && {
              customer_name: userData.claims.core.customer.name,
            }),
            ...(userData?.claims?.core?.sessionId && {
              session_id: userData.claims.core.sessionId,
            }),
            ...(userlaneEmail && {
              user_email: userlaneEmail,
              customer_domain: userlaneEmail.split('@')[1],
            }),
            ...(userData?.name && { user_name: userData.name }),
            ...(userData?.claims?.core?.dbUserId != null && {
              db_user_id: userData.claims.core.dbUserId,
            }),
            ...(userData?.claims?.googleCustomerId && {
              google_customer_id: userData.claims.googleCustomerId,
            }),
            ...(userData?.claims?.cdwCustomerId && {
              cdw_customer_id: userData.claims.cdwCustomerId,
            }),
            ...(userData?.claims?.core?.features?.length && {
              features: [...userData.claims.core.features],
            }),
            ...(permissionCodes.length && {
              permission_codes: permissionCodes,
            }),
          });
        })
      ),
    { dispatch: false }
  );

  tokenValidationFailed$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserIdentitySettingsActions.tokenValidationFailed),
        tap(({ error }) => {
          this.router.navigate(['/welcome'], {
            queryParams: { authError: encodeURIComponent(error) },
          });
        })
      ),
    { dispatch: false }
  );
}
